import type { DesignItem, ImageTransform } from '../types';
import type { LegacyCropMetadata, NormalizedImageTransform } from './types';

const DEFAULT_ADJUSTMENTS = {
  brightness: 1,
  contrast: 1,
  saturation: 1,
  hueRotate: 0,
  blur: 0,
};

const DEFAULT_CROP = {
  x: 0,
  y: 0,
  width: 1,
  height: 1,
  scale: 1,
};

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function positive(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Convert legacy pixel-based imageCropper metadata to normalized 0..1 source rect.
 */
export function legacyCropToNormalized(
  legacy: LegacyCropMetadata | undefined,
  item: DesignItem,
  sourceW: number,
  sourceH: number,
): ImageTransform['crop'] | undefined {
  if (!legacy || legacy.enabled === false) return undefined;

  const sw = Math.max(1, sourceW);
  const sh = Math.max(1, sourceH);
  const itemW = Math.max(1, positive(item.width, 1));
  const itemH = Math.max(1, positive(item.height, 1));
  const scale = positive(legacy.scale, 1);
  const maskW = positive(legacy.maskWidth, itemW);
  const maskH = positive(legacy.maskHeight, itemH);

  // Legacy crop uses CSS transform translate + scale on object-fit:contain image.
  // Approximate visible source region from mask dimensions and pan offset.
  const visibleW = Math.min(1, (maskW / itemW) / scale);
  const visibleH = Math.min(1, (maskH / itemH) / scale);
  const panX = Number(legacy.x) || 0;
  const panY = Number(legacy.y) || 0;
  const panNormX = panX / sw;
  const panNormY = panY / sh;

  const x = clamp01(0.5 - visibleW / 2 - panNormX);
  const y = clamp01(0.5 - visibleH / 2 - panNormY);
  const width = clamp01(Math.min(visibleW, 1 - x));
  const height = clamp01(Math.min(visibleH, 1 - y));

  if (width >= 0.999 && height >= 0.999 && scale <= 1.001) {
    return undefined;
  }

  return { x, y, width, height, scale };
}

/**
 * Build a v1 imageTransform from the item's current legacy `imageCropper`
 * (pixel pan + zoom) while preserving any existing adjustments/effects.
 *
 * Used to persist the editor's crop into the authoritative pipeline contract
 * when a gesture-based editor (mobile crop/erase overlay) commits, so preview,
 * canvas export, SVG export and PHP print all read the same source rectangle.
 */
export function legacyCropToImageTransform(item: DesignItem): ImageTransform {
  const ex = item as Record<string, unknown>;
  const existing = (ex.imageTransform && typeof ex.imageTransform === 'object'
    ? { ...(ex.imageTransform as object) }
    : {}) as Partial<ImageTransform>;
  const legacy = ex.imageCropper as LegacyCropMetadata | undefined;
  const { width: sourceW, height: sourceH } = getSourceDimensions(item);
  const crop = legacyCropToNormalized(legacy, item, sourceW, sourceH);

  return {
    version: 1,
    crop: crop ?? undefined,
    adjustments: existing.adjustments,
    effects: existing.effects ?? { preset: 'none' },
  };
}

export function getSourceDimensions(item: DesignItem): { width: number; height: number } {
  const ex = item as Record<string, unknown>;
  const w = positive(ex.sourcePixelWidth, 0);
  const h = positive(ex.sourcePixelHeight, 0);
  if (w > 0 && h > 0) return { width: w, height: h };
  return { width: Math.max(1, positive(item.width, 1)), height: Math.max(1, positive(item.height, 1)) };
}

export function normalizeImageTransform(item: DesignItem): NormalizedImageTransform {
  const ex = item as Record<string, unknown>;
  const raw = ex.imageTransform as Partial<ImageTransform> | undefined;
  const legacy = ex.imageCropper as LegacyCropMetadata | undefined;
  const { width: sourceW, height: sourceH } = getSourceDimensions(item);

  let crop = { ...DEFAULT_CROP };
  if (raw?.crop && raw.version === 1) {
    crop = {
      x: clamp01(Number(raw.crop.x) || 0),
      y: clamp01(Number(raw.crop.y) || 0),
      width: clamp01(Number(raw.crop.width) || 1),
      height: clamp01(Number(raw.crop.height) || 1),
      scale: positive(raw.crop.scale, 1),
    };
  } else {
    const fromLegacy = legacyCropToNormalized(legacy, item, sourceW, sourceH);
    if (fromLegacy) {
      crop = {
        x: clamp01(fromLegacy.x),
        y: clamp01(fromLegacy.y),
        width: clamp01(fromLegacy.width),
        height: clamp01(fromLegacy.height),
        scale: positive(fromLegacy.scale, 1),
      };
    }
  }

  type Adjustments = NonNullable<ImageTransform['adjustments']>;
  const adj: Partial<Adjustments> = raw?.adjustments ?? {};
  const adjustments: Adjustments = {
    brightness: positive(adj.brightness, DEFAULT_ADJUSTMENTS.brightness),
    contrast: positive(adj.contrast, DEFAULT_ADJUSTMENTS.contrast),
    saturation: positive(adj.saturation, DEFAULT_ADJUSTMENTS.saturation),
    hueRotate: Number.isFinite(Number(adj.hueRotate)) ? Number(adj.hueRotate) : DEFAULT_ADJUSTMENTS.hueRotate,
    blur: Number.isFinite(Number(adj.blur)) ? Math.max(0, Number(adj.blur)) : DEFAULT_ADJUSTMENTS.blur,
  };

  const preset = String(raw?.effects?.preset ?? 'none').trim() || 'none';

  return {
    version: 1,
    crop,
    adjustments,
    effects: { preset },
  };
}

export function isLayerVisible(item: DesignItem): boolean {
  const ex = item as Record<string, unknown>;
  return ex.visible !== false;
}

const LEGACY_FILTER_TO_PRESET: Record<string, string> = {
  'grayscale(100%)': 'grayscale',
  'sepia(100%)': 'sepia',
  'sepia(35%) contrast(1.1) brightness(0.95) saturate(0.85)': 'vintage',
  'sepia(20%) saturate(1.25) hue-rotate(-8deg)': 'warm',
  'saturate(0.9) hue-rotate(12deg) brightness(1.03)': 'cool',
};

function normalizeFilterString(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Migrate legacy item.filter CSS strings to imageTransform.effects.preset.
 * Clears item.filter after migration.
 */
export function migrateItemImageTransform(item: DesignItem): DesignItem {
  const ex = item as Record<string, unknown>;
  const rawFilter = typeof ex.filter === 'string' ? ex.filter.trim() : '';
  const hasTransform = ex.imageTransform && typeof ex.imageTransform === 'object';

  if (!rawFilter && hasTransform) {
    return item;
  }

  const next = { ...item } as Record<string, unknown>;
  const existing = (hasTransform ? { ...(ex.imageTransform as object) } : {}) as Partial<ImageTransform>;

  if (rawFilter && !existing.effects?.preset) {
    const preset = LEGACY_FILTER_TO_PRESET[normalizeFilterString(rawFilter)] ?? 'none';
    existing.version = 1;
    existing.effects = { preset };
  }

  if (Object.keys(existing).length > 0) {
    next.imageTransform = {
      version: 1,
      crop: existing.crop,
      adjustments: existing.adjustments,
      effects: existing.effects ?? { preset: 'none' },
    };
  }

  if (rawFilter) {
    delete next.filter;
  }

  return next as DesignItem;
}
